import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ReactIcons, SimpleButton } from "@kalyzee/kalyzee-ui";
import { ContentLocationOutputDTO, MeetingOutputDTO, VodOutputDTO } from "@kalyzee/kast-web-sdk/http";
import { FullscreenContainer, FullscreenContainerRef, Loading, MultipleVideoPlayer, MultipleVideoPlayerDateDisplayMode, MultipleVideoPlayerDebugState, MultipleVideoPlayerRef, MultipleVideoPlayerRenderingContext, PlayerRenderItem, VideoSourceDated } from "@kalyzee/kast-web-sdk/web-components";
import { useSdkContext } from "../../../../contexts/sdk/context";
import { useSettings } from "../../../../hooks/settings";
import { MultiVideoPlayerDebugPane } from "../../../../components/debug/MultiVideoPlayerDebugPane";
import { useAppStoreSelector } from "../../../../contexts/appStore/selectors";
import { focusIconForLayout, NewTriLayoutContainer, NewTriLayoutContainerProps } from "../../../../components/player/container/NewTriLayoutContainer";
import { PlayerItem } from "../../../../components/player/interfaces";
import { PlayerActionItem, PlayerWorkBox, PlayerWorkBoxOverlayContent } from "../../../../components/workBoxes/PlayerWorkBox";
import { LayoutSelector } from "../../../../components/common/LayoutSelector";
import { LayoutType } from "../../../../interfaces/ui";

import "./player.css";
import styles from "./player.module.css";
import clsx from "clsx";

const VOD_LAYOUTS = [LayoutType.Full, LayoutType.Top, LayoutType.Right] as const;

export interface VodPlayerContentPageProps {
  id: string;
  onBack?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

export const VodPlayerContentPage = ({ id, onBack, className, style }: VodPlayerContentPageProps) => {
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [meeting, setMeeting] = useState<MeetingOutputDTO>();
  const [vods, setVods] = useState<VodOutputDTO[]>([]);
  const [debugState, setDebugState] = useState<MultipleVideoPlayerDebugState | null>(null);
  const playerRef = useRef<MultipleVideoPlayerRef>();
  const sdkCtx = useSdkContext();
  const [settings] = useSettings();

  const loadVods = async () => {
    if (!id) {
      setError("Internal error");
      return;
    }
    setLoading(true);
    try {
      const meeting = await sdkCtx.client.getHttpClient().meeting.getById(id);
      if (!meeting.data) throw new Error('Meeting not found');
      setMeeting(meeting.data);
      const baseUrl = sdkCtx.client.getHttpClient().getBaseURL();
      const res = await sdkCtx.client.getHttpClient().client.get(`${baseUrl}${baseUrl.endsWith("/") ? "" : "/"}meeting/${id}/vods?vodFilters=state[eq]terminated`, {});
      if (res.status === 200) {
        setError(undefined);
        setVods(res.data ?? []);
      } else {
        setError("Une erreur est survenue");
      }
    } catch (err) {
      setError("Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVods();
  }, []);

  useEffect(() => {
    if (!settings.dev) return;
    const id = setInterval(() => {
      const state = playerRef.current?.getDebugState();
      if (state) setDebugState(state);
    }, 500);
    return () => clearInterval(id);
  }, [settings.dev]);

  const data = useMemo<VideoSourceDated[]>(() => {
    const result: VideoSourceDated[] = [];
    vods.forEach((v) => {
      const isHls = (c?: ContentLocationOutputDTO) => c?.videoType === "hls";
      const getPriority = (c: ContentLocationOutputDTO) => isHls(c) ? 1 : 0;
      const contentLocations = v.contentLocations?.sort((a, b) => getPriority(b) - getPriority(a));
      const contentLocation = contentLocations?.length ? contentLocations[0] : undefined;
      const src = contentLocation?.videoUrl;
      const hls = isHls(contentLocation);
      const device = (v as any).device;
      const name = device?.name;
      if (!contentLocation || !src || !name) return;
      result.push({
        id: v.id,
        src: hls ? { type: "hls", src } : src,
        name,
        startedAt: new Date(v.recordStartedAt),
        endedAt: new Date(v.recordEndedAt),
      });
    });
   return result;
  }, [vods]);

  const info = useMemo<React.ReactNode>(() => {
    if (!meeting) return undefined;
    const expert = meeting.actors?.find((a) => a.externalProvider === "escaet:organizer");
    const expertName = expert?.identity?.username ?? expert?.user?.identity?.username ?? "";
    const meetingName = meeting.name;
    return (
      <div className={styles.infoContainer}>
        <div className={styles.meetingName}>{meetingName}</div>
        <div className={styles.expertName}>{expertName}</div>
      </div>
    );
  }, [meeting]);

  const layout = useAppStoreSelector((state) => state.layout);
  const isMobile = useAppStoreSelector((state) => state.isMobile);
  const [fullscreenItemId, setFullscreenItemId] = useState<string | undefined>();

  // Native fullscreen control inside the timeline is disabled; we drive it from
  // each player's overlay action instead.
  const controlsProps = useMemo(() => ({ header: info, displayFullscreen: false }), [info]);
  const watermarkProps = useMemo(() => ({ element: <ReactIcons.LogoFull />, bottom: 20, right: 30 }), []);

  const fullscreenContainerRef = useRef<FullscreenContainerRef>(null);

  // Sync browser fullscreen with the controlled state. Pressing Esc fires
  // onFullScreen(false), so we clear the item when that happens.
  const onFullscreenChange = useCallback((enabled: boolean) => {
    if (!enabled) setFullscreenItemId(undefined);
  }, []);

  const triLayoutRenderItem = useCallback<NonNullable<NewTriLayoutContainerProps["renderItem"]>>(
    (rendererProvider, { item, isInStrip, isFullscreen, onFocus }) => {
      const data = item.getData();
      if (data.type !== "vod_player") return null;
      const label = data.player.source.name ?? "";

      const overlayContent: PlayerWorkBoxOverlayContent = isInStrip ? {
        type: "selection",
        layout,
        onSelect: onFocus,
      } : undefined;

      // Fullscreen toggle lives in the topbar.
      const topbarActions: PlayerActionItem[] = [];
      if (!isFullscreen) {
        topbarActions.push({
          key: "fullscreen",
          icon: <ReactIcons.Expand />,
          onClick: () => setFullscreenItemId(item.getId()),
          label: "Plein écran",
        });
      } else {
        topbarActions.push({
          key: "exit-fullscreen",
          icon: <ReactIcons.Shrink />,
          onClick: () => setFullscreenItemId(undefined),
          label: "Quitter le plein écran",
        });
      }
      return rendererProvider((children) => (
        <PlayerWorkBox
          label={label}
          video
          loading={data.player.stalling}
          broken={data.player.broken}
          isSmall={isInStrip}
          overlayMode="overlay-hoverable"
          topbarActions={topbarActions}
          overlayContent={overlayContent}
          topBarClassName={styles.playerTopBar}
        >
          {children}
        </PlayerWorkBox>
      ));
    },
    [layout],
  );

  const renderPlayersLayout = useCallback(
    (players: PlayerRenderItem[], ctx: MultipleVideoPlayerRenderingContext) => {
      const items = players.map((player) => new PlayerItem({ type: "vod_player", player, hidden: !player.display }));
      return (
        <div className={clsx(isMobile ? styles.playersLayoutMobile : styles.playersLayout, { [styles.hideControls]: !ctx.displayControls })}>
          {!fullscreenItemId && (
            <LayoutSelector
              className={styles.layoutSelector}
              orientation={isMobile ? "horizontal" : "vertical"}
              layouts={VOD_LAYOUTS}
              defaultLayout={LayoutType.Top}
            />
          )}
          <div className={styles.playersLayoutContent}>
            <NewTriLayoutContainer
              items={items}
              layout={layout}
              fullscreenItemId={fullscreenItemId}
              renderItem={triLayoutRenderItem}
              style={{ padding: fullscreenItemId ? 0 : 16, boxSizing: "border-box" }}
            />
          </div>
        </div>
      );
    },
    [isMobile, layout, fullscreenItemId, triLayoutRenderItem],
  );

  if (loading) return <Loading fullscreen message="Chargement en cours..." style={style} />;

  const renderContent = () => {
    if (error) {
      return (
        <div className={styles.errorContainer}>
          <div className={styles.errorTitle}>Une erreur est survenue</div>
          <SimpleButton label="Réessayer" onClick={() => loadVods()} />
        </div>
      );
    }
    return (
      <FullscreenContainer
        ref={fullscreenContainerRef}
        useNative
        fullscreen={!!fullscreenItemId}
        onFullScreen={onFullscreenChange}
        style={{ width: "100%", height: "100%" }}
      >
        <>
          <MultipleVideoPlayer
            ref={playerRef}
            className={styles.vods}
            controls={controlsProps}
            //watermark={watermarkProps}
            dateDisplayMode={MultipleVideoPlayerDateDisplayMode.NONE}
            data={data}
            renderPlayersLayout={renderPlayersLayout}
          />
          <MultiVideoPlayerDebugPane state={debugState} className={styles.debugOverlay} />
        </>
      </FullscreenContainer>
    );
  };
  const classes = [styles.container];
  if (className) classes.push(className);
  return (
    <div className={classes.join(" ")} style={style}>
      {renderContent()}
    </div>
  );
};

